/**
 * Migration Report Render Model
 *
 * Single source of truth for the typed contract between the browser pipeline,
 * the interactive HTML report, and the server-side PDF/DOCX renderers.
 *
 * Phase 0 (contract first): this module defines the TypeScript types and a
 * zod schema with the caps and closed enums documented in the historical
 * hybrid-proposal planning brief (§5; preserved in git history — see
 * docs/archived-components.md).
 *
 * Design rules (see proposal §5.1):
 * - All text fields are plain text. No HTML, no Markdown. Renderers apply formatting.
 * - All enumerations are closed sets. Validator rejects unknown values.
 * - Every collection has an explicit maximum length. Every string has an explicit
 *   maximum character count.
 * - Diagrams are transported as browser-rendered PNG data URLs only.
 *   SVG transport is rejected at the schema layer in the initial implementation.
 * - Closed-enum public-access reason codes mirror §7.8 of the proposal.
 *
 * This module is browser-safe: it has no Node-only imports.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Caps and limits (proposal §5.3)
// ---------------------------------------------------------------------------

/** Total request body cap (proxy + Express body-parser); kept here as a reference constant. */
export const MAX_REQUEST_BODY_BYTES = 25 * 1024 * 1024;

/** Maximum number of top-level sections in a report. */
export const MAX_SECTIONS = 64;

/** Maximum nesting depth for subsections. */
export const MAX_SECTION_DEPTH = 4;

/** Maximum number of subsections at any single level. */
export const MAX_SUBSECTIONS_PER_SECTION = 64;

/** Maximum number of tables attached to a single section. */
export const MAX_TABLES_PER_SECTION = 32;

/** Maximum number of rows in any single table. */
export const MAX_ROWS_PER_TABLE = 2000;

/** Maximum number of total rows across the entire report (counted by the validator). */
export const MAX_TOTAL_ROWS = 50000;

/** Maximum number of linked navigation items in a section. */
export const MAX_LINKED_ITEMS_PER_SECTION = 2000;

/** Maximum nesting depth for linked-item trees. */
export const MAX_LINKED_ITEM_DEPTH = 3;

/** Maximum number of diagrams in a report. Generous because graph-only
 *  diagrams are tiny (node/edge metadata only) and a single service
 *  may contribute one overview plus one neighborhood diagram per
 *  mapped component. PNG payloads remain bounded by
 *  {@link MAX_AGGREGATE_DIAGRAM_BYTES}. */
export const MAX_DIAGRAMS = 1024;

/** Per-cell maximum text length. */
export const MAX_CELL_TEXT = 4000;

/** Per-warning maximum text length. */
export const MAX_WARNING_TEXT = 2000;

/** Per section body / content maximum text length. */
export const MAX_SECTION_BODY_TEXT = 16000;

/** Per-diagram-asset maximum bytes (1 MB) for the decoded PNG. */
export const MAX_DIAGRAM_BYTES = 1024 * 1024;

/** Aggregate diagram asset bytes per report (8 MB). */
export const MAX_AGGREGATE_DIAGRAM_BYTES = 8 * 1024 * 1024;

/** Maximum diagram dimension (width or height) in pixels. */
export const MAX_DIAGRAM_PIXELS = 8000;

/** Maximum graph nodes for the structured DiagramGraph companion. */
export const MAX_DIAGRAM_NODES = 5000;

/** Maximum graph edges for the structured DiagramGraph companion. */
export const MAX_DIAGRAM_EDGES = 10000;

/** Maximum length of the data URL string itself (base64 overhead included). */
export const MAX_DIAGRAM_DATA_URL_CHARS = Math.ceil(MAX_DIAGRAM_BYTES * 4 / 3) + 64;

// ---------------------------------------------------------------------------
// Closed enums (proposal §5.2, §7.8)
// ---------------------------------------------------------------------------

/** Velocity deployment target. Must match {@link import('./velocity.js').TargetPlatform}. */
export const targetPlatformSchema = z.enum(['velocity-online', 'velocity-enterprise']);
export type RenderTargetPlatform = z.infer<typeof targetPlatformSchema>;

/** Migration strategy. Must match {@link import('./api.js').MigrationStrategy}. */
export const migrationStrategySchema = z.enum(['side-by-side', 'in-place']);
export type RenderMigrationStrategy = z.infer<typeof migrationStrategySchema>;

/** Severity classification for warning rows. */
export const warningSeveritySchema = z.enum(['info', 'warning', 'manual-action', 'unmapped']);
export type WarningSeverity = z.infer<typeof warningSeveritySchema>;

/**
 * Closed enum of public-access probe reason codes (proposal §7.8).
 * The user-facing wording is rendered by the report renderers; this enum is
 * the contract that travels through the model.
 */
export const probeReasonCodeSchema = z.enum([
  'verified-public',
  'requires-authentication',
  'not-found',
  'mixed-content-blocked',
  'internal-or-unroutable-host',
  'network-or-cors-blocked',
  'timeout',
  'unexpected-response',
  'probe-disabled',
]);
export type ProbeReasonCode = z.infer<typeof probeReasonCodeSchema>;

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Plain-text constraint. Disallows control characters except tab, newline,
 * and carriage return so renderers do not have to defend against escape
 * sequences or bidi overrides leaking through.
 */
const plainText = (maxLength: number) =>
  z.string()
    .max(maxLength)
    // eslint-disable-next-line no-control-regex
    .refine((value) => !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u2066-\u2069\u202A-\u202E]/.test(value), {
      message: 'Plain-text fields may not contain control characters or bidi overrides',
    });

/** Slug/anchor identifier. */
const anchorIdSchema = z.string().min(1).max(128).regex(/^[a-zA-Z0-9_-]+$/, {
  message: 'Identifiers must contain only letters, digits, hyphens, and underscores',
});

/** Numeric scalar that disallows NaN/Infinity. */
const finiteInt = (max: number) =>
  z.number().int().min(0).max(max);

// ---------------------------------------------------------------------------
// Tables, sections, warnings
// ---------------------------------------------------------------------------

export const reportTableRowSchema = z.object({
  parameter: plainText(MAX_CELL_TEXT),
  value: plainText(MAX_CELL_TEXT),
}).strict();
export type ReportTableRow = z.infer<typeof reportTableRowSchema>;

export const reportTableSchema = z.object({
  title: plainText(200),
  rows: z.array(reportTableRowSchema).max(MAX_ROWS_PER_TABLE),
}).strict();
export type ReportTable = z.infer<typeof reportTableSchema>;

export const linkedItemSchema: z.ZodType<LinkedItem> = z.lazy(() =>
  z.object({
    label: plainText(400),
    anchorId: anchorIdSchema,
    children: z.array(linkedItemSchema).max(MAX_LINKED_ITEMS_PER_SECTION).optional(),
  }).strict(),
);
export interface LinkedItem {
  label: string;
  anchorId: string;
  children?: LinkedItem[];
}

/**
 * Reference to a diagram embedded elsewhere in the model's `diagrams` array.
 * Sections reference diagrams by id; full asset bytes live in
 * `MigrationReportModel.diagrams` to make total-byte enforcement cheap.
 */
export const diagramRefSchema = z.object({
  diagramId: anchorIdSchema,
}).strict();
export type DiagramRef = z.infer<typeof diagramRefSchema>;

/**
 * Per-section metadata used by the interactive HTML report (filter chips
 * + search). Round-trips through the render model so component sections
 * carry their kind/status/type and search terms to the server-side HTML
 * generator. Ignored by PDF and DOCX generators.
 */
export const sectionMetadataSchema = z.object({
  kind: z.enum(['component', 'service', 'custom', 'arcade', 'contextual']).optional(),
  status: z.enum(['mapped', 'partial', 'unmapped', 'manual-required']).optional(),
  componentType: z.enum(['input', 'output', 'filter', 'processor']).optional(),
  searchTerms: z.array(plainText(200)).max(64).optional(),
}).strict();
export type SectionMetadata = z.infer<typeof sectionMetadataSchema>;

export const reportSectionSchema: z.ZodType<ReportSection> = z.lazy(() =>
  z.object({
    title: plainText(400),
    content: plainText(MAX_SECTION_BODY_TEXT),
    anchorId: anchorIdSchema.optional(),
    subsections: z.array(reportSectionSchema).max(MAX_SUBSECTIONS_PER_SECTION).optional(),
    tables: z.array(reportTableSchema).max(MAX_TABLES_PER_SECTION).optional(),
    linkedItems: z.array(linkedItemSchema).max(MAX_LINKED_ITEMS_PER_SECTION).optional(),
    diagram: diagramRefSchema.optional(),
    metadata: sectionMetadataSchema.optional(),
  }).strict(),
);
export interface ReportSection {
  title: string;
  content: string;
  anchorId?: string;
  subsections?: ReportSection[];
  tables?: ReportTable[];
  linkedItems?: LinkedItem[];
  diagram?: DiagramRef;
  metadata?: SectionMetadata;
}

export const warningRowSchema = z.object({
  severity: warningSeveritySchema,
  componentRef: plainText(400),
  message: plainText(MAX_WARNING_TEXT),
  /** Optional public-access probe reason code (proposal §7.8). */
  probeReasonCode: probeReasonCodeSchema.optional(),
}).strict();
export type WarningRow = z.infer<typeof warningRowSchema>;

// ---------------------------------------------------------------------------
// Diagrams
// ---------------------------------------------------------------------------

/**
 * Optional structured graph companion used by the interactive HTML report and
 * for accessibility metadata. The server does NOT render this in Phase 1;
 * server-side rendering uses the PNG `dataUrl` only.
 */
export const diagramGraphSchema = z.object({
  nodes: z.array(z.object({
    id: anchorIdSchema,
    label: plainText(400),
    kind: plainText(64).optional(),
  }).strict()).max(MAX_DIAGRAM_NODES),
  edges: z.array(z.object({
    from: anchorIdSchema,
    to: anchorIdSchema,
    label: plainText(400).optional(),
  }).strict()).max(MAX_DIAGRAM_EDGES),
}).strict();
export type DiagramGraph = z.infer<typeof diagramGraphSchema>;

/**
 * Diagram asset.
 *
 * Transports either a browser-rendered PNG (as a data URL) or a structured
 * graph payload that the server rasterizes on receipt via the existing
 * flow-renderer (node-canvas). At least one of `dataUrl` or `graph` must be
 * present. When `dataUrl` is supplied the server validates the data-URL
 * prefix, PNG magic bytes (89 50 4E 47 0D 0A 1A 0A), declared dimensions,
 * and total byte budget before embedding. SVG and other formats are
 * rejected at the schema layer.
 */
export const diagramAssetSchema = z.object({
  id: anchorIdSchema,
  title: plainText(400),
  /** PNG data URL: `data:image/png;base64,...`. PNG-only by design. */
  dataUrl: z.string()
    .min(`data:image/png;base64,`.length + 1)
    .max(MAX_DIAGRAM_DATA_URL_CHARS)
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/, {
      message: 'dataUrl must be a base64-encoded PNG data URL',
    })
    .optional(),
  widthPx: finiteInt(MAX_DIAGRAM_PIXELS).optional(),
  heightPx: finiteInt(MAX_DIAGRAM_PIXELS).optional(),
  /** Structured graph payload. Required when `dataUrl` is omitted. */
  graph: diagramGraphSchema.optional(),
}).strict().refine((d) => Boolean(d.dataUrl) || Boolean(d.graph), {
  message: 'diagram must supply dataUrl or graph',
});
export type DiagramAsset = z.infer<typeof diagramAssetSchema>;

// ---------------------------------------------------------------------------
// Top-level model
// ---------------------------------------------------------------------------

/** Current schema version. Server must reject any other value. */
export const RENDER_MODEL_SCHEMA_VERSION = 1 as const;

export const renderFormatSchema = z.enum(['pdf', 'docx', 'html']);
export type RenderFormat = z.infer<typeof renderFormatSchema>;

export const migrationReportSummarySchema = z.object({
  totalComponents: finiteInt(1_000_000),
  fullyMapped: finiteInt(1_000_000),
  partiallyMapped: finiteInt(1_000_000),
  unmapped: finiteInt(1_000_000),
  manualActions: finiteInt(1_000_000),
}).strict();
export type MigrationReportSummary = z.infer<typeof migrationReportSummarySchema>;

export const migrationReportModelSchema = z.object({
  schemaVersion: z.literal(RENDER_MODEL_SCHEMA_VERSION),
  /** Plain-text display title used by the renderers. */
  title: plainText(400),
  /** ISO-8601 generation timestamp; renderers convert to display format. */
  generatedAtIso: z.iso.datetime({ offset: true }).or(z.iso.datetime()),
  /** Display-only, sanitized source filename. */
  sourceFilenameDisplay: plainText(255).optional(),
  targetPlatform: targetPlatformSchema,
  migrationStrategy: migrationStrategySchema,
  summary: migrationReportSummarySchema,
  sections: z.array(reportSectionSchema).max(MAX_SECTIONS),
  warnings: z.array(warningRowSchema).max(2000).optional(),
  diagrams: z.array(diagramAssetSchema).max(MAX_DIAGRAMS).optional(),
}).strict();
export type MigrationReportModel = z.infer<typeof migrationReportModelSchema>;

export const renderRequestSchema = z.object({
  model: migrationReportModelSchema,
  format: renderFormatSchema,
}).strict();
export type RenderRequest = z.infer<typeof renderRequestSchema>;

// ---------------------------------------------------------------------------
// Cross-field validation helpers (used by the server-side validator entry)
// ---------------------------------------------------------------------------

/**
 * Walks the section tree and returns total rows across all tables and the
 * maximum nesting depth seen. Used by the server validator to enforce
 * caps that zod alone cannot express ergonomically.
 */
export function summarizeStructure(model: MigrationReportModel): {
  totalRows: number;
  maxSectionDepth: number;
  maxLinkedItemDepth: number;
  totalDiagramBytes: number;
} {
  let totalRows = 0;
  let maxSectionDepth = 0;
  let maxLinkedItemDepth = 0;

  const walkLinked = (items: LinkedItem[] | undefined, depth: number): void => {
    if (!items) return;
    if (depth > maxLinkedItemDepth) maxLinkedItemDepth = depth;
    for (const item of items) walkLinked(item.children, depth + 1);
  };

  const walkSection = (section: ReportSection, depth: number): void => {
    if (depth > maxSectionDepth) maxSectionDepth = depth;
    if (section.tables) {
      for (const table of section.tables) totalRows += table.rows.length;
    }
    walkLinked(section.linkedItems, 1);
    if (section.subsections) {
      for (const sub of section.subsections) walkSection(sub, depth + 1);
    }
  };

  for (const section of model.sections) walkSection(section, 1);

  let totalDiagramBytes = 0;
  if (model.diagrams) {
    for (const diagram of model.diagrams) {
      if (!diagram.dataUrl) continue;
      // Estimate decoded byte length from the base64 body of the data URL.
      const commaIdx = diagram.dataUrl.indexOf(',');
      const base64 = commaIdx >= 0 ? diagram.dataUrl.slice(commaIdx + 1) : '';
      // Each 4 base64 chars decode to 3 bytes; subtract padding.
      const padding = (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
      const decoded = Math.max(0, Math.floor(base64.length * 3 / 4) - padding);
      totalDiagramBytes += decoded;
    }
  }

  return { totalRows, maxSectionDepth, maxLinkedItemDepth, totalDiagramBytes };
}
